<template>
  <div class="space-y-4">
    <h2 class="text-center text-lg font-medium text-[var(--plugin-charcoal)]">
      {{ t('plugin.dateStep.title') }}
    </h2>

    <!-- Fixed workshop date notice -->
    <p
      v-if="(product?.type === 'workshop' || product?.is_workshop) && product?.workshop_datetime"
      class="text-[var(--plugin-gray-500)]"
    >
      {{ t('plugin.dateStep.fixedWorkshopDate') }}: {{ formatDateTime(product.workshop_datetime) }}
    </p>

    <!-- Normal date/time selection: calendar always visible, slots area shows loader or content -->
    <template v-else>
      <div class="space-y-5">
        <!-- Calendar header: month + year + week navigation (visible during loading) -->
        <div class="flex items-center justify-between">
          <div class="text-sm font-medium text-[var(--plugin-charcoal)]">
            {{ monthYearLabel }}
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--plugin-border)] bg-card text-[var(--plugin-charcoal)] hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[var(--plugin-border)]"
              :disabled="!canGoToPreviousWeek"
              @click="shiftWeek(-1)"
            >
              ‹
            </button>
            <button
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--plugin-border)] bg-card text-[var(--plugin-charcoal)] hover:border-primary"
              @click="shiftWeek(1)"
            >
              ›
            </button>
          </div>
        </div>

        <!-- Week days row: number on top, day shortcut below (like Flutter) -->
        <div class="grid grid-cols-7 gap-1 text-center text-xs">
          <div v-for="day in weekDays" :key="day.key" class="flex flex-col items-center gap-1">
            <button
              type="button"
              class="flex h-11 w-11 items-center justify-center rounded-full text-lg transition"
              :class="dayButtonClass(day)"
              :disabled="!day.isInRange || day.isPast"
              :title="!day.hasSlots && day.isInRange && !day.isPast ? t('plugin.dateStep.noSlotsForDay') : undefined"
              @click="selectDate(day.dateKey)"
            >
              {{ day.dayOfMonth }}
            </button>
            <span
              class="text-[11px] uppercase tracking-wide"
              :class="day.hasSlots && !day.isPast ? 'text-[var(--plugin-charcoal)]' : 'text-[var(--plugin-gray-400)]'"
            >
              {{ day.weekdayLabel }}
            </span>
          </div>
        </div>

        <!-- Time slots area: spinner when loading, error/empty/slots otherwise -->
        <div class="flex min-h-[120px] flex-col items-center justify-center py-6">
          <template v-if="loading">
            <div
              class="h-10 w-10 animate-spin rounded-full border-4 border-[var(--plugin-gray-200)] border-t-primary"
              aria-label="Loading"
            />
          </template>
          <template v-else-if="error">
            <p class="text-center text-sm text-red-600">
              {{ error }}
            </p>
          </template>
          <template v-else>
            <div v-if="currentSlots.length === 0" class="text-center text-xs text-[var(--plugin-gray-500)]">
              {{ t('plugin.dateStep.noSlotsForDay') }}
            </div>

            <div v-else class="w-full space-y-4">
            <section v-if="morningSlots.length" class="space-y-2">
              <h3 class="text-left text-sm font-medium text-[var(--plugin-charcoal)]">
                {{ t('plugin.dateStep.morning') }}
              </h3>
              <div class="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-3">
                <button
                  v-for="slot in morningSlots"
                  :key="slotKey(slot)"
                  type="button"
                  class="flex min-w-0 flex-col items-center rounded-[12px] border border-transparent bg-[var(--plugin-gray-100)] px-2 py-2 text-center text-xs text-[var(--plugin-charcoal)] transition hover:border-primary sm:min-w-[96px] sm:px-3"
                  :class="timeButtonClass(slot)"
                  @click="selectTime(slot)"
                >
                  <span class="text-sm font-medium">
                    {{ slot.timeLabel }}
                  </span>
                  <span
                    v-if="!isZlavaDna && slot.finalPrice != null && slot.finalPrice > 0"
                    class="mt-1 flex items-center gap-1 text-[11px] font-medium"
                    :class="slotPriceClass(slot)"
                  >
                    <span>{{ slotPriceArrow(slot) }}</span>
                    <span>€{{ slot.finalPrice.toFixed(2) }}</span>
                  </span>
                </button>
              </div>
            </section>

            <section v-if="afternoonSlots.length" class="space-y-2">
              <h3 class="text-left text-sm font-medium text-[var(--plugin-charcoal)]">
                {{ t('plugin.dateStep.afternoon') }}
              </h3>
              <div class="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-3">
                <button
                  v-for="slot in afternoonSlots"
                  :key="slotKey(slot)"
                  type="button"
                  class="flex min-w-0 flex-col items-center rounded-[12px] border border-transparent bg-[var(--plugin-gray-100)] px-2 py-2 text-center text-xs text-[var(--plugin-charcoal)] transition hover:border-primary sm:min-w-[96px] sm:px-3"
                  :class="timeButtonClass(slot)"
                  @click="selectTime(slot)"
                >
                  <span class="text-sm font-medium">
                    {{ slot.timeLabel }}
                  </span>
                  <span
                    v-if="!isZlavaDna && slot.finalPrice != null && slot.finalPrice > 0"
                    class="mt-1 flex items-center gap-1 text-[11px] font-medium"
                    :class="slotPriceClass(slot)"
                  >
                    <span>{{ slotPriceArrow(slot) }}</span>
                    <span>€{{ slot.finalPrice.toFixed(2) }}</span>
                  </span>
                </button>
              </div>
            </section>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { getAvailabilityForDate } from '@/api/public'
import { useWizardState, setSelectedDateTime } from '@/composables/useWizardState'
import { t, getDateLocale } from '@/i18n'

interface SlotWithStartEnd {
  start: string
  end: string
  employee_id?: number
  basePrice?: number
  finalPrice?: number
}

const props = defineProps<{
  branchId: number
  product: {
    id: number
    is_workshop?: boolean
    workshop_datetime?: string
    type?: 'service' | 'package' | 'workshop'
    duration_minutes?: number
    duration?: number
  } | null
  source?: string
}>()
const isZlavaDna = computed(() => props.source === 'zlavadna')
const state = useWizardState()
const availability = ref<Array<{ date: string; slots: SlotWithStartEnd[] }>>([])
const loading = ref(true)
const error = ref<string | null>(null)

const weekAvailabilityCache = new Map<string, Array<{ date: string; slots: SlotWithStartEnd[] }>>()

const product = computed(() => props.product)

const today = new Date()
today.setHours(0, 0, 0, 0)
const todayStr = toLocalDateStr(today)
const todayWeekStart = getWeekStart(new Date())

const weekStart = ref<Date>(new Date(todayWeekStart.getTime()))
const initialSelectedDate =
  state.selectedDate && state.selectedDate >= todayStr
    ? state.selectedDate
    : todayStr
const selectedDate = ref<string>(initialSelectedDate)

const availableDateKeys = computed<Set<string>>(() => {
  const set = new Set<string>()
  for (const day of availability.value) {
    if (day.slots.length > 0) {
      set.add(day.date)
    }
  }
  return set
})

function slotTimeLabel(slot: SlotWithStartEnd): string {
  try {
    return new Date(slot.start).toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return slot.start.slice(11, 16)
  }
}

function formatDateTime(iso: string) {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(dateLocale, { dateStyle: 'medium' }) + ' ' + d.toLocaleTimeString(dateLocale, { timeStyle: 'short' })
  } catch {
    return iso
  }
}

function getWeekStart(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay() // 0 (Sun) - 6 (Sat)
  const diff = (day + 6) % 7 // make Monday start (0)
  d.setDate(d.getDate() - diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function toLocalDatetimeStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}:${s}`
}

function buildSlotStartEnd(date: string, timeStr: string, durationMinutes: number): SlotWithStartEnd {
  const normalizedTime = timeStr.length === 5 ? timeStr : timeStr.slice(0, 5)
  const startStr = `${date}T${normalizedTime}:00`
  const start = new Date(startStr)
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000)
  return {
    start: toLocalDatetimeStr(start),
    end: toLocalDatetimeStr(end),
  }
}

function firstDayWithSlotsInWeek(
  weekStartDate: Date,
  daysWithSlots: Array<{ date: string }>,
  today: string
): string | null {
  const set = new Set(daysWithSlots.map((d) => d.date))
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStartDate)
    d.setDate(d.getDate() + i)
    const dateKey = toLocalDateStr(d)
    if (dateKey >= today && set.has(dateKey)) return dateKey
  }
  return null
}

const dateLocale = getDateLocale()

const monthYearLabel = computed(() => {
  try {
    const d = new Date(selectedDate.value)
    return d.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })
  } catch {
    return selectedDate.value
  }
})

const weekDays = computed(() => {
  const days = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart.value)
    d.setDate(d.getDate() + i)
    const dateKey = toLocalDateStr(d)
    const weekdayLabel = d.toLocaleDateString(dateLocale, { weekday: 'short' })
    const isPast = dateKey < todayStr
    days.push({
      key: dateKey,
      dateObj: d,
      dateKey,
      dayOfMonth: d.getDate(),
      weekdayLabel,
      isInRange: true,
      isPast,
      hasSlots: availableDateKeys.value.has(dateKey),
    })
  }
  return days
})

const canGoToPreviousWeek = computed(() => {
  return weekStart.value.getTime() > todayWeekStart.getTime()
})

const currentSlots = computed(() => {
  const day = availability.value.find((d) => d.date === selectedDate.value)
  return day?.slots.map((s) => ({
    slot: s,
    date: day.date,
    timeLabel: slotTimeLabel(s),
    basePrice: s.basePrice,
    finalPrice: s.finalPrice,
  })) ?? []
})

const morningSlots = computed(() => {
  return currentSlots.value.filter((entry) => entry.slot.start.slice(11, 16) < '12:00')
})

const afternoonSlots = computed(() => {
  return currentSlots.value.filter((entry) => entry.slot.start.slice(11, 16) >= '12:00')
})

function slotKey(entry: { slot: SlotWithStartEnd; date: string }) {
  return `${entry.date}_${entry.slot.start}`
}

function isSlotSelected(entry: { slot: SlotWithStartEnd; date: string }) {
  const s = state.selectedSlot
  return (
    !!s &&
    s.start === entry.slot.start &&
    s.end === entry.slot.end &&
    state.selectedDate === entry.date
  )
}

function selectTime(entry: { slot: SlotWithStartEnd; date: string }) {
  const time = entry.slot.start.slice(11, 16)
  setSelectedDateTime(entry.date, time, { ...entry.slot })
  selectedDate.value = entry.date
}

function dayButtonClass(day: {
  dateKey: string
  dateObj: Date
  isInRange: boolean
  isPast?: boolean
}) {
  const isSelected = selectedDate.value === day.dateKey
  const hasAvailability = availableDateKeys.value.has(day.dateKey)

  if (!day.isInRange || day.isPast) {
    return 'cursor-default text-[var(--plugin-gray-400)] opacity-60'
  }

  if (isSelected) {
    return 'bg-primary text-white'
  }

  if (hasAvailability) {
    return 'bg-card text-[var(--plugin-charcoal)] border border-[var(--plugin-border)] hover:border-primary'
  }

  return 'bg-transparent text-[var(--plugin-gray-400)]'
}

function timeButtonClass(entry: {
  slot: SlotWithStartEnd
  date: string
  timeLabel: string
  basePrice?: number
  finalPrice?: number
}) {
  return isSlotSelected(entry)
    ? 'ring-2 ring-primary'
    : ''
}

function slotPriceClass(entry: {
  basePrice?: number
  finalPrice?: number
}) {
  const { basePrice, finalPrice } = entry
  if (basePrice == null || finalPrice == null) {
    return 'text-[var(--plugin-gray-500)]'
  }
  if (finalPrice > basePrice) {
    return 'text-red-600'
  }
  if (finalPrice < basePrice) {
    return 'text-green-600'
  }
  return 'text-[var(--plugin-gray-500)]'
}

function slotPriceArrow(entry: {
  basePrice?: number
  finalPrice?: number
}) {
  const { basePrice, finalPrice } = entry
  if (basePrice == null || finalPrice == null) return ''
  if (finalPrice > basePrice) return '↑'
  if (finalPrice < basePrice) return '↓'
  return ''
}

function selectDate(dateKey: string) {
  if (dateKey < todayStr) return
  selectedDate.value = dateKey
}

function shiftWeek(offset: number) {
  if (offset < 0 && !canGoToPreviousWeek.value) return
  const newStart = new Date(weekStart.value)
  newStart.setDate(newStart.getDate() + offset * 7)
  weekStart.value = getWeekStart(newStart)

  const weekEnd = new Date(weekStart.value)
  weekEnd.setDate(weekEnd.getDate() + 6)
  const weekStartStr = toLocalDateStr(weekStart.value)
  const weekEndStr = toLocalDateStr(weekEnd)
  if (selectedDate.value < weekStartStr || selectedDate.value > weekEndStr) {
    selectedDate.value = selectedDate.value < todayStr ? todayStr : weekStartStr
  }
}

watch(
  () => [
    props.branchId,
    props.product?.id,
    props.product?.type === 'workshop' || props.product?.is_workshop,
    props.product?.type,
    props.product?.duration_minutes ?? props.product?.duration,
    weekStart.value.getTime(),
    isZlavaDna.value,
  ] as const,
  async ([branchId, productId, isWorkshop, productType, durationMinutes]) => {
    if (!branchId || !productId || isWorkshop) {
      loading.value = false
      availability.value = []
      return
    }
    const duration = durationMinutes ?? 60
    const weekStartStr = toLocalDateStr(weekStart.value)
    const cacheKey = `${branchId}|${productId}|${productType ?? 'service'}|${weekStartStr}|${isZlavaDna.value}`

    const cached = weekAvailabilityCache.get(cacheKey)
    if (cached !== undefined) {
      availability.value = cached
      error.value = null
      loading.value = false
      const firstWithSlots = firstDayWithSlotsInWeek(weekStart.value, cached, todayStr)
      selectedDate.value = firstWithSlots ?? (selectedDate.value < todayStr ? todayStr : selectedDate.value)
      return
    }

    loading.value = true
    error.value = null
    try {
      const weekEnd = new Date(weekStart.value)
      weekEnd.setDate(weekEnd.getDate() + 6)
      const weekEndStr = toLocalDateStr(weekEnd)
      const requestDate = weekStartStr >= todayStr ? weekStartStr : todayStr

      const res = await getAvailabilityForDate({
        branch_id: branchId,
        service_id: productType === 'service' ? productId : undefined,
        package_id: productType === 'package' ? productId : undefined,
        date: requestDate,
        include_prices: !isZlavaDna.value,
      })

      const rawSlots = (res as { available_slots?: unknown[]; data?: { available_slots?: unknown[] } }).data?.available_slots
        ?? (res as { available_slots?: unknown[] }).available_slots
        ?? []
      const days: Array<{ date: string; slots: SlotWithStartEnd[] }> = []
      for (const dayData of rawSlots as Array<{ date?: string; pricing_groups?: Array<{ base_price?: number; final_price?: number; slots?: unknown[] }> }>) {
        const dateStr = dayData.date
        if (!dateStr || dateStr < weekStartStr || dateStr > weekEndStr) continue
        if (dateStr < todayStr) continue
        if (!dayData.pricing_groups?.length) continue
        const slots: SlotWithStartEnd[] = []
        for (const pg of dayData.pricing_groups) {
          const basePrice = pg.base_price
          const finalPrice = pg.final_price
          for (const raw of pg.slots ?? []) {
            const item = raw as string | { start?: string }
            const timeStr = typeof item === 'string' ? item : item.start?.slice(11, 16) ?? ''
            if (!timeStr) continue
            const slot = buildSlotStartEnd(dateStr, timeStr, duration)
            slots.push({ ...slot, basePrice, finalPrice })
          }
        }
        if (slots.length) days.push({ date: dateStr, slots })
      }
      weekAvailabilityCache.set(cacheKey, days)
      availability.value = days
      const firstWithSlots = firstDayWithSlotsInWeek(weekStart.value, days, todayStr)
      selectedDate.value = firstWithSlots ?? (selectedDate.value < todayStr ? todayStr : selectedDate.value)
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('plugin.error.load')
    } finally {
      loading.value = false
    }
  },
  { immediate: true }
)
</script>
